import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './ChatComment.module.scss';
import { Icon, Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  setEditedCommentAction,
  setIsBeingEditedMessageAction,
} from '../../../actions/commentsActions/commentsActions';
import {
  deleteDocumentCommentAction,
  downloadAttachedFile,
} from '../../../actions/commentsActions/documentsCommentsActions/documentsCommentsActions';
import moment from 'moment';
import classNames from 'classnames';
import { formatBytes } from '../commentsFooter/attachedFiles/AttachedCommonFiles/utils/formatBytes';
import LightboxGroup from '../../LightboxGroup/LightboxGroup';
import { linkify } from '../../../helpers/linkify';

const { confirm } = Modal;

const ChatComment = ({
  belongsToCurrentUser = false,
  commentData,
  lightboxConfig = {},
}) => {
  const dispatch = useDispatch();
  const showPreviewRef = useRef();
  const [attachedImages, setAttachedImages] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const isBeingEdited = useSelector(
    (state) => state.commentsChatStore.isCommentBeingEdited
  );
  const currentComment = useSelector(
    (state) => state.commentsChatStore.currentComment
  );
  const entityId = useSelector((state) => state.commentsChatStore.entityId);
  const editedCommentId = useSelector(
    (state) => state.commentsChatStore.editedComment.id
  );

  useEffect(() => {
    if (showPreviewRef.current) {
      showPreviewRef.current.focus();
    }
  }, [showPreview, showPreviewRef]);

  const startEditingComment = () => {
    dispatch(
      setEditedCommentAction({
        id: commentData.noteId || commentData.id,
        message: commentData.note,
        attachments: commentData.attachments,
      })
    );
    dispatch(setIsBeingEditedMessageAction(true));
  };

  const confirmDeleteMessage = () => {
    confirm({
      title: 'Вы действительно хотите удалить этот комментарий?',
      content: 'Его нельзя будет восстановить.',
      onOk() {
        dispatch(deleteDocumentCommentAction(commentData.noteId || commentData.id));
      },
      onCancel() {},
    });
  };

  const confirmEditMessage = () => {
    if (
      currentComment.message ||
      currentComment.attachments.images.length ||
      currentComment.attachments.files.length
    ) {
      confirm({
        title: 'Вы действительно хотите редактировать этот комментарий?',
        onOk() {
          startEditingComment();
        },
        onCancel() {},
      });
    } else {
      startEditingComment();
    }
  };

  const formatCommentDate = (dateString) => {
    return moment(dateString).format('DD.MM.YYYY HH:mm');
  };

  const urlFromBlob = (string) => {
    const data = Buffer.from(string, 'base64');
    return window.URL.createObjectURL(new Blob([data]));
  };

  const createImages = useCallback(async () => {
    if (commentData.attachments?.images) {
      const images = commentData.attachments.images?.map((image) => {
        return {
          name: image.name,
          src: urlFromBlob(image.file),
        };
      });
      setAttachedImages(images);
    } else {
      setAttachedImages([]);
    }
  }, [commentData]);

  useEffect(() => {
    createImages();
  }, [commentData]);

  const clickImage = (currentImageIndex) => {
    setShowPreview(true);
    const clickedImage = commentData.attachments.images[currentImageIndex];
    setCurrentImageIndex(currentImageIndex);
  };

  const downloadFile = useCallback(
    (fileName, name) => {
      dispatch(downloadAttachedFile(fileName, name));
    },
    [entityId, commentData]
  );

  const handleClosePreview = useCallback(() => {
    setShowPreview(false);
  }, []);

  const messageStyles = useMemo(() => {
    const messageStyles = [];
    if (commentData.isSystemMessage) {
      messageStyles.push(styles.systemMessage);
    } else if (belongsToCurrentUser) {
      messageStyles.push(styles.belongsToCurrentUser);
    } else {
      messageStyles.push(styles.doesntBelongToCurrentUser);
    }
    if (
      isBeingEdited &&
      (editedCommentId === commentData.noteId ||
        editedCommentId === commentData.id)
    ) {
      messageStyles.push(styles.currentlyEditedMessage);
    }
    return messageStyles;
  }, [commentData, isBeingEdited]);

  return (
    <div
      comment-data-item="true"
      className={classNames(styles.chatComment, ...messageStyles)}
    >
      {!commentData.isSystemMessage && belongsToCurrentUser ? (
        <div className={styles.messageOptionIcons}>
          <div
            className={styles.messageOptionIcon}
            onClick={confirmEditMessage}
          >
            <Icon type="edit" />
          </div>
          <div
            className={styles.messageOptionIcon}
            onClick={confirmDeleteMessage}
          >
            <Icon type="delete" theme={'filled'} />
          </div>
        </div>
      ) : null}
      <div className={styles.chatMessageContent}>
        {attachedImages.length ? (
          <div className={styles.attachedImages}>
            {attachedImages?.map((image, index) => {
              return (
                <img
                  key={image.name}
                  src={image.src}
                  alt={'img'}
                  className={styles.attachedImagePreview}
                  onClick={() => clickImage(index, image)}
                />
              );
            })}
            {showPreview && (
              <div autofocus={true} ref={showPreviewRef} tabIndex={0}>
                <LightboxGroup
                  onClose={handleClosePreview}
                  visible={showPreview}
                  attachments={commentData.attachments.images}
                  attachment={commentData.attachments.images[currentImageIndex]}
                  url={lightboxConfig.url}
                  params={lightboxConfig.params}
                  imageParamField={'fileName'}
                />
              </div>
            )}
          </div>
        ) : null}
        <div
          className={styles.text}
          dangerouslySetInnerHTML={{
            __html: linkify(commentData?.note || ''),
          }}
        />
        {commentData.attachments?.files?.length ? (
          <div className={styles.attachedFiles}>
            {commentData.attachments?.files?.map((file) => {
              return (
                <div
                  key={file.fileName}
                  className={styles.attachedFile}
                  onClick={() => downloadFile(file.fileName, file.name)}
                >
                  <div className={styles.attachedFileIcon}>
                    <Icon type={'paper-clip'} />
                  </div>
                  <div className={styles.fileNameAndSize}>
                    <div>{file.name}</div>
                    <div className={styles.fileSize}>
                      {formatBytes(file.size, 1)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        <div className={styles.footer}>
          <div>
            {commentData?.operationDate || commentData?.date
              ? formatCommentDate(
                  commentData.operationDate || commentData?.date
                )
              : null}
          </div>
          {belongsToCurrentUser && 0 ? (
            <div>
              <Icon type="eye-invisible" />
              <span>Скрыто от собственника</span>
            </div>
          ) : null}
          <div>
            {commentData.worker?.fullName || commentData.user?.fullName}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(ChatComment);
